import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';

export interface TokenInfo {
  isValid: boolean;
  expiresAt: Date | null;
  timeUntilExpiry: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private readonly _tokenSubject = new BehaviorSubject<string | null>(null);
  
  readonly token$ = this._tokenSubject.asObservable().pipe(distinctUntilChanged());
  readonly isAuthenticated$ = this.token$.pipe(
    map(token => this.isTokenValid(token)),
    distinctUntilChanged()
  );

  setAccessToken(token: string | null): void {
    this._tokenSubject.next(token);
  }

  getAccessToken(): string | null {
    return this._tokenSubject.value;
  }

  clearToken(): void {
    this._tokenSubject.next(null);
  }

  isAccessTokenExpired(): boolean {
    const token = this.getAccessToken();
    return !this.isTokenValid(token);
  }

  getTokenInfo(): TokenInfo {
    const token = this.getAccessToken();
    
    if (!token) {
      return this._createEmptyTokenInfo();
    }

    try {
      const payload = this._decodeToken(token);
      return this._createTokenInfoFromPayload(payload);
    } catch {
      return this._createEmptyTokenInfo();
    }
  }

  private isTokenValid(token: string | null): boolean {
    if (!token) {
      return false;
    }

    try {
      const payload = this._decodeToken(token);
      return this._isPayloadValid(payload);
    } catch {
      return false;
    }
  }

  private _decodeToken(token: string): any {
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decoded);
  }

  private _isPayloadValid(payload: any): boolean {
    if (!payload.exp) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return payload.exp > now;
  }

  private _createEmptyTokenInfo(): TokenInfo {
    return { isValid: false, expiresAt: null, timeUntilExpiry: null };
  }

  private _createTokenInfoFromPayload(payload: any): TokenInfo {
    const expiresAt = payload.exp ? new Date(payload.exp * 1000) : null;
    const timeUntilExpiry = expiresAt ? expiresAt.getTime() - Date.now() : null;
    const isValid = timeUntilExpiry ? timeUntilExpiry > 0 : false;

    return { isValid, expiresAt, timeUntilExpiry };
  }
}